import React, { useEffect, useState } from 'react';
import { Button, Space, Form, message, Row, Col, Select, Input, Alert, Modal } from 'antd';
import { OrderModel } from '@app/interfaces/order';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { transferCustomerOrder } from '@app/store/slices/orderSlice';
import { MdCancel } from 'react-icons/md';
import { GrRevert } from 'react-icons/gr';
import useDebounce from '@app/hooks/useDebounce';
import { getCustomers } from '@app/store/slices/customerSlice';
import { RootState } from '@app/store/store';
import { FaMapMarked } from 'react-icons/fa';
import AddressAutocomplete from '@app/components/common/AddressAutocomplete';

interface OrderTransferCustomerFormProps {
  open: boolean;
  record?: OrderModel;
  onClose: () => void;
}

interface CustomerState {
  id?: number;
  name?: string;
  address?: string;
}

const OrderTransferCustomerForm: React.FC<OrderTransferCustomerFormProps> = ({ open, record, onClose }) => {
  const [form] = Form.useForm();
  const [customer, setCustomer] = useState<CustomerState>({});
  const [searchTermCustomer, setSearchTermCustomer] = useState<string>('');

  const debouncedSearchTermCustomer = useDebounce(searchTermCustomer, 100);
  const { data: customers = [] } = useAppSelector((state: RootState) => state.customer || {});

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (record) {
      form.setFieldsValue(record);
    }
  }, [record]);

  useEffect(() => {
    form.setFieldsValue({ delivery_address: customer?.address || record?.delivery_address });
  }, [customer, form]);

  useEffect(() => {
    fetchCustomers();
  }, [debouncedSearchTermCustomer, dispatch]);

  const fetchCustomers = async () => {
    const newParams = { name_or_phone_or_customer_code_cont: debouncedSearchTermCustomer };
    await dispatch(getCustomers(newParams));
  };

  const onSearchCustomer = (value: string) => {
    console.log('search:', value);
    setSearchTermCustomer(value);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      await dispatch(transferCustomerOrder({ orderId: Number(record?.id), formData: values })).unwrap();

      onClose();
    } catch (error: any) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('An unknown error occurred');
      }
    }
  };

  const onChangeCustomer = (value: string) => {
    form.setFieldsValue({ customer_id: value });

    const findCustomer = customers.find((item) => item.id === Number(value));
    setCustomer({
      id: findCustomer?.id,
      name: findCustomer?.name,
      address: findCustomer?.full_address,
    });
  };

  const customerOptions =
    customers && customers.map((item) => ({ value: item.id, label: `${item.customer_code}-${item.name}` }));

  return (
    <>
      <Modal open={open} onCancel={onClose} style={{ top: 20 }} closable={true} footer={null}>
        <h3 className="mb-4">
          <span className="font-bold-weight">Chuyển đơn khách hàng</span>
        </h3>

        <Alert
          message="Warning"
          description="Khi chuyển đơn cho khách hàng khác, toàn bộ công nợ khách hàng nếu có sẽ chuyển sang cho khách hàng mới. Bạn có chắc chắn?"
          type="warning"
          banner
          closable
          className="text-warning my-3"
        />

        <Form form={form} name="form-order" className="order-container">
          <Row>
            <Col span={7}>Khách hàng</Col>
            <Col span={17}>
              <Form.Item name="customer_id" rules={[{ required: true, message: 'Please select a customer!' }]}>
                <Select
                  allowClear
                  showSearch
                  placeholder="Chọn khách hàng"
                  optionFilterProp="children"
                  onChange={onChangeCustomer}
                  onSearch={onSearchCustomer}
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={customerOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={7}>
              <FaMapMarked className="me-1 text-warning" /> Địa chỉ giao hàng
            </Col>
            <Col span={17}>
              <Form.Item name="delivery_address">
                {/* Đổi địa chỉ ở đây thì toạ độ được tra lại ở nền (Delivery::GeocodeOrderAddressJob). */}
                <AddressAutocomplete rows={3} placeholder="Địa chỉ giao hàng" />
              </Form.Item>
            </Col>
          </Row>

          <Row className="mt-4 text-right">
            <Col span={24}>
              <Space>
                <Button type="primary" onClick={handleSubmit}>
                  <GrRevert /> Chuyển đơn
                </Button>
                <Button type="primary" danger onClick={onClose}>
                  <MdCancel /> Huỷ
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default OrderTransferCustomerForm;
